// ITER283c-fix — Shared brief-form fields: broad-list cuisines +
// segments with CLICK-TO-ASSIGN pattern (1..N numbered pills per row,
// no drag). Plus multi-select format (max 2) with orphan-value
// visibility, plus free-text custom cuisine entry with case-insensitive
// de-duplication against BOTH existing entries and canonical chip
// labels. Consumed by BOTH CityIntelligence.js and
// ExpansionIntelligenceHome.js via BriefRankedFieldsAdapter.jsx.
//
// Contract with the parent (via adapter):
//   props.value = {
//     cuisines_ranked: string[]  // client labels in rank order (max 5)
//     segments_ranked: string[]  // segment ids in rank order (max 5)
//     formats:         string[]  // format ids (max 2, ordered)
//   }
//   props.onChange = (patch) => void   // partial-merge back into parent
//   props.segmentOptions = [{id, label}]   // engine's segment vocabulary
//   props.formatOptions  = [{id, label}]   // engine's format vocabulary
//
// Every selected cuisine/segment ALWAYS carries a rank (no unranked
// selected chips). Ranks are contiguous 1..N where N = current
// selection count. Clicking a numbered pill k on a row pushes the row
// to position k, sliding intermediate rows to fill the vacated slot.
import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  CUISINE_CHOICES,
  normalizeCuisineLabel,
  resolveLabelToClientChip,
} from "../../config/cuisineChoices";

const MAX_RANK = 5;
const MAX_FORMATS = 2;

function Chip({ selected, onClick, children, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={
        "px-3 py-1.5 rounded-full text-sm border transition " +
        (selected
          ? "bg-slate-900 text-amber-200 border-slate-900"
          : disabled
            ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
            : "bg-white text-slate-700 border-slate-300 hover:border-slate-500")
      }
    >
      {children}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────
   Click-to-assign ranked list. Every row has 1..N numbered pills.
   Clicking pill k on a row moves that row to position k-1 (0-based),
   sliding the intermediate rows to fill the vacated slot ("push"
   semantics).
   ──────────────────────────────────────────────────────────────── */
function RankedList({
  title, ranked, onReorder, onRemove, weightHint, testidPrefix, labelFor,
}) {
  const N = ranked.length;
  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= N) return;
    const next = ranked.slice();
    const [row] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, row);
    onReorder(next);
  };
  return (
    <div className="mt-4" data-testid={`${testidPrefix}-ranked-block`}>
      <div className="text-sm font-medium text-slate-800 mb-1">
        {title}
      </div>
      <div className="text-xs text-slate-500 mb-2">
        Tap the number that reflects the priority of each pick.
        #1 carries the most weight in the analysis. {weightHint}
      </div>
      <ol className="space-y-1.5">
        {ranked.map((item, i) => (
          <li
            key={item}
            data-testid={`${testidPrefix}-ranked-row-${i}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50 border border-slate-200"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-300 text-slate-900 text-xs font-bold shrink-0">
              {i < MAX_RANK ? i + 1 : "·"}
            </span>
            <span className="flex-1 text-sm">{labelFor ? labelFor(item) : item}</span>
            <div className="flex items-center gap-1" data-testid={`${testidPrefix}-rank-pills-${i}`}>
              {Array.from({ length: N }).map((_, k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => move(i, k)}
                  disabled={k === i}
                  data-testid={`${testidPrefix}-set-rank-${i}-to-${k + 1}`}
                  aria-label={`Set rank ${k + 1}`}
                  className={
                    "w-6 h-6 text-[11px] leading-none rounded-full border transition font-semibold " +
                    (k === i
                      ? "bg-slate-900 text-amber-200 border-slate-900 cursor-default"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-800 hover:bg-slate-100")
                  }
                >
                  {k + 1}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onRemove(item)}
              className="p-1 text-slate-500 hover:text-slate-900 shrink-0"
              data-testid={`${testidPrefix}-remove-${i}`}
              aria-label="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </li>
        ))}
        {ranked.length === 0 && (
          <li className="text-xs italic text-slate-500">
            Nothing selected yet — pick from the options above.
          </li>
        )}
      </ol>
    </div>
  );
}

export default function BriefRankedFields({ value, onChange,
                                             segmentOptions,
                                             formatOptions }) {
  const {
    cuisines_ranked = [],
    segments_ranked = [],
    formats = [],
  } = value || {};

  const [customCuisine, setCustomCuisine] = useState("");

  // ── Cuisines ────────────────────────────────────────────────
  // Chip click adds to end if not present (cap at MAX_RANK); removes
  // if already present. Every selected cuisine ALWAYS carries a rank.
  const toggleCuisine = (label) => {
    const norm = normalizeCuisineLabel(label);
    const existingIdx = cuisines_ranked.findIndex(
      (x) => normalizeCuisineLabel(x) === norm,
    );
    if (existingIdx >= 0) {
      const next = cuisines_ranked.filter((_, i) => i !== existingIdx);
      onChange({ cuisines_ranked: next });
      return;
    }
    if (cuisines_ranked.length >= MAX_RANK) return;
    onChange({ cuisines_ranked: [...cuisines_ranked, label] });
  };
  const isCuisineSelected = (chipLabel) => {
    const norm = normalizeCuisineLabel(chipLabel);
    return cuisines_ranked.some((x) => normalizeCuisineLabel(x) === norm);
  };

  const addCustomCuisine = () => {
    const raw = customCuisine.trim();
    if (!raw) return;
    // 1. Resolve typed text to a chip if the normalised form matches.
    //    Example: "conti" doesn't match; "continental european" (case-
    //    insensitive) matches the Continental European chip → select
    //    that chip instead of adding a duplicate free-text row.
    const chip = resolveLabelToClientChip(raw);
    if (chip) {
      if (!isCuisineSelected(chip.label)) {
        if (cuisines_ranked.length < MAX_RANK) {
          onChange({ cuisines_ranked: [...cuisines_ranked, chip.label] });
        }
      }
      setCustomCuisine("");
      return;
    }
    // 2. De-dup against existing entries (normalised).
    const norm = normalizeCuisineLabel(raw);
    if (cuisines_ranked.some((x) => normalizeCuisineLabel(x) === norm)) {
      setCustomCuisine("");
      return;
    }
    // 3. Add as a new free-text entry (top-5 cap enforced).
    if (cuisines_ranked.length >= MAX_RANK) {
      setCustomCuisine("");
      return;
    }
    onChange({ cuisines_ranked: [...cuisines_ranked, raw] });
    setCustomCuisine("");
  };
  const removeCuisineFromRank = (label) => {
    onChange({
      cuisines_ranked: cuisines_ranked.filter((x) => x !== label),
    });
  };
  const reorderCuisines = (next) => onChange({ cuisines_ranked: next });

  // ── Segments ────────────────────────────────────────────────
  const toggleSegment = (id) => {
    const existingIdx = segments_ranked.findIndex(
      (x) => String(x).toLowerCase() === String(id).toLowerCase(),
    );
    if (existingIdx >= 0) {
      const next = segments_ranked.filter((_, i) => i !== existingIdx);
      onChange({ segments_ranked: next });
      return;
    }
    if (segments_ranked.length >= MAX_RANK) return;
    onChange({ segments_ranked: [...segments_ranked, id] });
  };
  const isSegmentSelected = (id) =>
    segments_ranked.some(
      (x) => String(x).toLowerCase() === String(id).toLowerCase(),
    );
  const removeSegmentFromRank = (id) => {
    onChange({
      segments_ranked: segments_ranked.filter((x) => x !== id),
    });
  };
  const reorderSegments = (next) => onChange({ segments_ranked: next });
  const segLabel = (id) =>
    (segmentOptions.find((s) => s.id === id) || {}).label || id;

  // ── Formats ─────────────────────────────────────────────────
  const toggleFormat = (id) => {
    const has = formats.includes(id);
    if (has) {
      onChange({ formats: formats.filter((x) => x !== id) });
      return;
    }
    if (formats.length >= MAX_FORMATS) return;
    onChange({ formats: [...formats, id] });
  };
  // Orphan formats: values persisted in the brief that no longer
  // appear in the current formatOptions vocabulary. Render them as
  // filled chips SO THE USER CAN SEE THEM AND REMOVE THEM — otherwise
  // the max-2 counter fills up mysteriously with no filled chip
  // visible (BUG 2 from Sprint 1c-fix).
  const orphanFormats = formats.filter(
    (f) => !formatOptions.some((o) => String(o.id) === String(f)),
  );

  return (
    <div className="space-y-8" data-testid="brief-ranked-fields">
      {/* ── Cuisines ─────────────────────────────────────── */}
      <section data-testid="brief-cuisines-section">
        <div className="text-sm font-semibold text-slate-800 mb-1">
          Cuisines
        </div>
        <div className="text-xs text-slate-500 mb-3">
          Pick up to {MAX_RANK} cuisines. Every pick appears in the
          ranked list below — use the numbered pills to set priority.
          #1 is treated as the primary cuisine in the analysis.
        </div>
        <div className="flex flex-wrap gap-2">
          {CUISINE_CHOICES.map((c) => {
            const selected = isCuisineSelected(c.label);
            const disabled = !selected && cuisines_ranked.length >= MAX_RANK;
            return (
              <Chip
                key={c.label}
                selected={selected}
                disabled={disabled}
                onClick={() => toggleCuisine(c.label)}
                testid={`cuisine-chip-${normalizeCuisineLabel(c.label)
                  .replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c.label}
              </Chip>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Input
            value={customCuisine}
            onChange={(e) => setCustomCuisine(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addCustomCuisine(); }
            }}
            placeholder="Add a custom cuisine (e.g. Naga, Parsi)…"
            className="max-w-xs"
            data-testid="cuisine-custom-input"
            disabled={cuisines_ranked.length >= MAX_RANK}
          />
          <Button
            type="button"
            variant="outline"
            onClick={addCustomCuisine}
            data-testid="cuisine-custom-add"
            disabled={cuisines_ranked.length >= MAX_RANK}
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
          {cuisines_ranked.length >= MAX_RANK && (
            <span className="text-xs text-slate-500"
                  data-testid="cuisine-max-hint">
              Maximum {MAX_RANK} cuisines. Remove one to add another.
            </span>
          )}
        </div>
        <RankedList
          title={`Selected cuisines (${cuisines_ranked.length}/${MAX_RANK})`}
          ranked={cuisines_ranked}
          onReorder={reorderCuisines}
          onRemove={removeCuisineFromRank}
          weightHint="Rank-1 cuisine carries full competitor weight; rank-5 counts at 20%."
          testidPrefix="cuisines"
        />
      </section>

      {/* ── Formats (max 2) ──────────────────────────────── */}
      <section data-testid="brief-formats-section">
        <div className="text-sm font-semibold text-slate-800 mb-1">
          Service Format
        </div>
        <div className="text-xs text-slate-500 mb-3">
          Pick one or two formats that best describe the concept.
          Selecting both (e.g. Casual Dining + Bar / Pub) is required
          for hybrid concepts so competitors match precisely.
        </div>
        {/* Orphan formats — persisted values not in the current
            vocabulary. Show them so the count doesn't lie. */}
        {orphanFormats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2"
               data-testid="format-orphans-block">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 self-center mr-1">
              From saved brief:
            </div>
            {orphanFormats.map((f) => (
              <button
                key={`orphan-${f}`}
                type="button"
                onClick={() => toggleFormat(f)}
                data-testid={`format-orphan-chip-${normalizeCuisineLabel(String(f)).replace(/[^a-z0-9]+/g, "-")}`}
                className="px-3 py-1.5 rounded-full text-sm border transition bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
              >
                {String(f)} <X className="inline w-3 h-3 ml-1" />
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {formatOptions.map((f) => {
            const selected = formats.includes(f.id);
            const disabled = !selected && formats.length >= MAX_FORMATS;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => toggleFormat(f.id)}
                disabled={disabled}
                data-testid={`format-chip-${f.id}`}
                className={
                  "px-3 py-1.5 rounded-full text-sm border transition " +
                  (selected
                    ? "bg-slate-900 text-amber-200 border-slate-900"
                    : disabled
                      ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-500")
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-xs text-slate-500"
             data-testid="format-count">
          {formats.length} of {MAX_FORMATS} formats selected.
          {formats.length >= MAX_FORMATS && (
            <span data-testid="format-max-hint">
              {" "}Remove one to change your picks.
            </span>
          )}
        </div>
      </section>

      {/* ── Target Segments ───────────────────────────────── */}
      <section data-testid="brief-segments-section">
        <div className="text-sm font-semibold text-slate-800 mb-1">
          Target Customer Segments
        </div>
        <div className="text-xs text-slate-500 mb-3">
          Pick up to {MAX_RANK} segments. Every pick appears in the
          ranked list below — set priority with the numbered pills.
          Your #1 segment is treated as non-negotiable in the demand
          gate.
        </div>
        <div className="flex flex-wrap gap-2">
          {segmentOptions.map((s) => {
            const selected = isSegmentSelected(s.id);
            const disabled = !selected && segments_ranked.length >= MAX_RANK;
            return (
              <Chip
                key={s.id}
                selected={selected}
                disabled={disabled}
                onClick={() => toggleSegment(s.id)}
                testid={`segment-chip-${s.id}`}
              >
                {s.label}
              </Chip>
            );
          })}
        </div>
        <RankedList
          title={`Selected segments (${segments_ranked.length}/${MAX_RANK})`}
          ranked={segments_ranked}
          onReorder={reorderSegments}
          onRemove={removeSegmentFromRank}
          weightHint="Rank-1 segment is the demand-gate non-negotiable — the analysis fails an area outright if this segment isn't evidenced."
          testidPrefix="segments"
          labelFor={segLabel}
        />
      </section>
    </div>
  );
}
